// Card preview respects the existing theme toggle and ignores touch input.
(() => {
  let active = null;
  let pending = 0;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  function reset() {
    if (!active) return;
    const { slideshow, index } = active;
    active = null;
    if (slideshow.isConnected) slideshow.select(index, undefined, { animate: !reduced.matches });
  }
  function preview(event) {
    const card = event.target instanceof Element ? event.target.closest('product-card') : null;
    const gallery = card?.querySelector('[data-soleil-hover-preview]');
    const rect = gallery?.getBoundingClientRect();
    const inside = rect && event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
    if (!inside) { reset(); return; }
    if (active?.gallery === gallery) return;
    reset();
    const slideshow = gallery.querySelector('slideshow-component');
    if (!slideshow || typeof slideshow.select !== 'function' || !slideshow.slides || slideshow.slides.length < 2) return;
    const index = slideshow.current;
    if (!Number.isInteger(index)) return;
    const next = (index + 1) % slideshow.slides.length;
    slideshow.slides[next]?.querySelector('img')?.removeAttribute('loading');
    active = { gallery, slideshow, index };
    slideshow.select(next, undefined, { animate: !reduced.matches });
  }
  document.addEventListener('pointermove', (event) => {
    if (event.pointerType !== 'mouse') return;
    cancelAnimationFrame(pending);
    pending = requestAnimationFrame(() => preview(event));
  }, { passive: true });
  document.addEventListener('pointerout', (event) => { if (!event.relatedTarget) { cancelAnimationFrame(pending); reset(); } });
  window.addEventListener('blur', reset);
  document.addEventListener('shopify:section:unload', reset);
})();

class SoleilGridThumbnails extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    this.links = [...this.querySelectorAll('a[href]')];
    this.targets = this.links.map((link) => document.getElementById(link.hash.slice(1)));
    this.desktop = matchMedia('(min-width: 750px)');
    const update = () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.updateCurrent());
    };
    // Desktop Horizon scrolls .page-wrapper; scroll does not bubble.
    document.addEventListener('scroll', update, { capture: true, passive: true, signal: this.abort.signal });
    window.addEventListener('resize', update, { passive: true, signal: this.abort.signal });
    this.addEventListener('click', (event) => {
      const link = event.target.closest('a');
      const index = this.links.indexOf(link);
      if (index < 0 || !this.desktop.matches || !this.targets[index]) return;
      event.preventDefault();
      this.targets[index].scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }, { signal: this.abort.signal });
    update();
  }
  updateCurrent() {
    if (!this.desktop.matches) return;
    // The editor can connect this element before the following gallery is parsed.
    this.links = [...this.querySelectorAll('a[href]')];
    this.targets = this.links.map((link) => document.getElementById(link.hash.slice(1)));
    const header = document.querySelector('header-component');
    const details = this.closest('.product-information')?.querySelector('.product-details > .group-block');
    this.style.setProperty('--soleil-details-top-padding', details ? getComputedStyle(details).paddingTop : '0px');
    const top = Math.max(0, header?.getBoundingClientRect().bottom || 0) + 16;
    this.closest('media-gallery')?.style.setProperty('--soleil-gallery-top', `${top}px`);
    let current = 0;
    this.targets.forEach((target, index) => {
      if (target && target.getBoundingClientRect().top <= top + innerHeight * 0.25) current = index;
    });
    this.links.forEach((link, index) => {
      if (index === current) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    });
    if (current !== this.current) {
      const rail = this.querySelector('nav');
      const link = this.links[current];
      if (rail && link) rail.scrollTop = Math.max(0, link.offsetTop - rail.offsetTop - rail.clientHeight / 2 + link.offsetHeight / 2);
      this.current = current;
    }
  }
  disconnectedCallback() { this.abort?.abort(); cancelAnimationFrame(this.frame); }
}
if (!customElements.get('soleil-grid-thumbnails')) customElements.define('soleil-grid-thumbnails', SoleilGridThumbnails);
